import { useEffect, useMemo, useState } from 'react';
import { KeenIcon } from '@/components/keenicons';
import demandLetterService from '@/services/letters/demandLetterService';
import { ChecklistPanel } from './ChecklistPanel';
import { CHANNELS } from './letterStatus';
import { parseEmails, requiresTracking, sendBlockers } from './sendGuards';

/**
 * Send modal: delivery methods, addresses, subject/message, tracking number,
 * the live checklist, and the reasons the button is disabled.
 */
const SendDemandModal = ({ open, onClose, letter, readiness, approvalRequired, onSend, sending = false }) => {
  const [via, setVia] = useState(['email']);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [tracking, setTracking] = useState('');
  const [remind, setRemind] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open || !letter) return;
    setVia(letter.recipient_email ? ['email'] : ['certified_mail']);
    setTo(letter.recipient_email || '');
    setCc('');
    setSubject(letter.email_subject || '');
    setMessage('');
    setTracking(letter.tracking_number || '');
    setRemind(true);
  }, [open, letter?.id]);

  useEffect(() => {
    if (!open || !letter) return;
    let cancelled = false;
    setChecking(true);
    demandLetterService.checklist(letter.id, via)
      .then((res) => !cancelled && setChecklist(res?.data || null))
      .catch(() => !cancelled && setChecklist(null))
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
  }, [open, letter?.id, via.join(',')]);

  const toList = useMemo(() => parseEmails(to), [to]);
  const ccList = useMemo(() => parseEmails(cc), [cc]);

  const blockers = useMemo(() => sendBlockers({
    status: letter?.status,
    fillIns: readiness?.fill_ins || [],
    missing: readiness?.missing || {},
    approvalRequired,
    via,
    to: toList,
    tracking,
  }).concat(checklist?.blocking ? ['The checklist has failing items'] : []), [letter?.status, readiness, approvalRequired, via, toList, tracking, checklist]);

  if (!open || !letter) return null;

  const toggle = (value) => setVia((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const submit = () => {
    onSend?.({
      sent_via: via,
      email_to: via.includes('email') ? toList : [],
      email_cc: via.includes('email') ? ccList : [],
      email_subject: subject || null,
      email_message: message || null,
      tracking_number: tracking || null,
      create_reminder: remind,
    });
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className="modal-animate-scale modal-body w-[95%] max-w-3xl relative">
        <h3 className="card-title mb-1">Send demand letter</h3>
        <p className="text-xs text-gray-500 mb-4">{letter.title}</p>
        <button type="button" onClick={onClose} className="modal-close-btn"><KeenIcon icon="cross" /></button>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="form-label mb-2">Delivery</label>
            <div className="space-y-2 mb-4">
              {CHANNELS.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="checkbox checkbox-sm" checked={via.includes(c.value)} onChange={() => toggle(c.value)} />
                  {c.label}
                </label>
              ))}
            </div>

            {via.includes('email') && (
              <>
                <div className="mb-3">
                  <label className="form-label mb-1">To</label>
                  <input className="input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="adjuster@carrier.com, second@carrier.com" />
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1">CC <span className="text-gray-400 text-xs">(you are always copied)</span></label>
                  <input className="input" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="paralegal@firm.com" />
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1">Subject</label>
                  <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1">Message</label>
                  <textarea className="input h-auto py-2" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short cover note. The letter itself is attached as a PDF." />
                </div>
              </>
            )}

            {requiresTracking(via) && (
              <div className="mb-3">
                <label className="form-label mb-1">Certified mail tracking number <span className="text-red-600">*</span></label>
                <input className="input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="9407 1111 2222 3333 4444 55" />
              </div>
            )}
            {!requiresTracking(via) && via.some((v) => v !== 'email') && (
              <div className="mb-3">
                <label className="form-label mb-1">Tracking / confirmation number <span className="text-gray-400 text-xs">(optional)</span></label>
                <input className="input" value={tracking} onChange={(e) => setTracking(e.target.value)} />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" className="checkbox checkbox-sm" checked={remind} onChange={(e) => setRemind(e.target.checked)} />
              Add tasks for the response deadline (and three days before)
            </label>
          </div>

          <div className="border-l border-gray-200 pl-6">
            <ChecklistPanel checklist={checklist} loading={checking} />
            {blockers.length > 0 && (
              <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800">
                <div className="font-semibold mb-1">Cannot send yet</div>
                <ul className="list-disc ml-4 space-y-0.5">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn btn-sm btn-light" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-sm btn-primary" disabled={sending || blockers.length > 0} onClick={submit}>
            <KeenIcon icon="sms" className="btn-icon" /> {sending ? 'Sending…' : 'Send now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { SendDemandModal };
